import type { RecommendationTarget } from '../api/endpoints.js';

/**
 * `target` names a SCREEN, not a URL (spec 2026-08-26 §6). Shared by every
 * surface that renders GET /v1/recommendations verbatim —
 * screens/protection.tsx (the full "Threats & actions" list) and
 * screens/dashboard.tsx (the same server list, plus two purely local cards —
 * see flow/actions.ts) both call this rather than each keeping its own copy.
 * Extracted out of flow/protection.ts once dashboard.tsx became a second
 * caller: a target→route mapping is exactly the kind of thing that drifts
 * into two disagreeing copies if it is left inline per screen.
 *
 * Every target the catalog can emit is listed here explicitly — `Partial`
 * rather than a `default: '#/…'` fallback, so a target this demo has no
 * screen for renders the card WITHOUT a link (never a fabricated route)
 * instead of silently landing somewhere wrong.
 *
 *  - `LikenessScoreDetail` and `LikenessHealthQuiz` both land on `#/quiz`
 *    (screens/quiz.js#QuizScreen): the score detail was folded into the
 *    Quiz & score screen, not given its own, when the score reads moved to
 *    GET /v1/me/score.
 *  - `Profile` and `ProfileOptional` both land on `#/account`
 *    (screens/profile.js): first/last name, email and the employer/school
 *    fields all live on that one screen.
 *  - `EmailVerification` has no entry: resending a verification email is an
 *    inline action (a button wired to POST /v1/me/email), not a screen of its
 *    own, so there is nowhere honest to link to — every caller special-cases
 *    this id rather than treating the missing href as an error.
 */
const RECOMMENDATION_HREF: Partial<Record<RecommendationTarget, string>> = {
  Photos: '#/photos',
  LikenessHealthQuiz: '#/quiz',
  LikenessScoreDetail: '#/quiz',
  Profile: '#/account',
  ProfileOptional: '#/account',
  Reports: '#/reports',
};

/**
 * `undefined` in, `undefined` out. A member read in the `ask` voice carries NO
 * target on any item, and the honest answer for "where does this card go" is
 * nowhere: the reader cannot perform the action. Callers must render such a
 * card as information rather than as navigation — never synthesise a route.
 */
export function recommendationHref(
  target: RecommendationTarget | undefined,
): string | undefined {
  return target === undefined ? undefined : RECOMMENDATION_HREF[target];
}
